import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Card, 
  Checkbox, 
  Button, 
  Modal, 
  Form, 
  Radio, 
  Select, 
  Space, 
  Typography, 
  Row, 
  Col, 
  Tag, 
  message,
  Tooltip,
  Divider
} from 'antd';
import { 
  SettingOutlined, 
  SaveOutlined, 
  AppstoreOutlined, 
  TeamOutlined,
  MobileOutlined,
  GlobalOutlined,
  CheckCircleOutlined,
  UserOutlined,
  ArrowLeftOutlined,
  CheckCircleFilled
} from '@ant-design/icons';
import Api from "../../../services/api";

const { Title, Text } = Typography;
const { Option } = Select;

function StudioUserPermission() {
  const { studio, userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [apps, setApps] = useState([]);
  const [appLabelMap, setAppLabelMap] = useState({});
  const [selectedApps, setSelectedApps] = useState([]);
  const [checkAll, setCheckAll] = useState(false);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [userLoaded, setUserLoaded] = useState(false);
  const [appsLoaded, setAppsLoaded] = useState(false);
  const isTargetOwner = userLoaded && !!(userInfo?.roles || []).some(r => String(r.code || '').toLowerCase().includes('owner'));

  
  const loadUserInfo = async () => {
    try {
      const result = await Api.get(`/api/admin/${userId}`);
      if (result?.data?.code === 0) {
        setUserInfo(result.data.data);
      }
      setUserLoaded(true);
    } catch (error) {
      message.error('Không thể tải thông tin user!');
      setUserLoaded(true);
    }
  };

  
  const loadApps = () => {
    const params = {
      target_user_id: userId
    };
    
    Api.get('/api/app_list', { params })
      .then(response => {
        if (response?.data?.code == 0) {
          const map = {};
          const appOptions = response.data.data.map(app => {
            const code = app.code || app.bundle_id || app.name;
            const os = app.os;
            const codeLabel = `${code}(${os})`;
            map[String(app.id)] = codeLabel;
            return {
              value: app.app_info_id || app.id, 
              label: codeLabel,
              code,
              os,
              name: app.name || code,
              codeLabel,
              thumb: app.thumb
            };
          });
          setApps(appOptions);
          setAppLabelMap(map);
          setAppsLoaded(true);
        }
      })
      .catch(error => {
        message.error('Không thể tải danh sách app!');
      });
  };

  
  const loadUserPermissions = () => {
    
    if (isTargetOwner) {
      const allIds = apps.map(a => String(a.value));
      setSelectedApps(allIds);
      setCheckAll(allIds.length === apps.length && apps.length > 0);
      setPermissionsLoaded(true);
      return;
    }

    Api.get(`/api/admin/${userId}/permissions`)
      .then(response => {
        if (response?.data?.code === 0) {
          const permissions = response.data.data || [];
          const appViewPerms = permissions.filter(p => p.code === 'app.view' && p.resource_type === 'app_info');
          const appIds = appViewPerms.flatMap(p => Array.isArray(p.resource_id) ? p.resource_id : [p.resource_id]);
          setSelectedApps(appIds.map(id => String(id)));
          setCheckAll(appIds.length === apps.length && apps.length > 0);
        }
        setPermissionsLoaded(true);
      })
      .catch(error => {
        message.error('Không thể tải quyền của user!');
        setPermissionsLoaded(true);
      });
  };

  
  const savePermissions = () => {
    setLoading(true);
    
    const permissions = [
      {
        code: 'app.view',
        resource_type: 'app_info',
        resource_id: selectedApps
      }
    ];

    Api.put(`/api/admin/${userId}/permissions`, { permissions })
      .then(response => {
        if (response?.data?.code === 0) {
          message.success('Cập nhật quyền thành công!');
        } else {
          message.error('Có lỗi xảy ra khi cập nhật quyền!');
        }
      })
      .catch(error => {
        message.error('Có lỗi xảy ra khi cập nhật quyền!');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  
  const handleAppChange = (checkedValues) => {
    setSelectedApps(checkedValues);
    setCheckAll(checkedValues.length === apps.length && apps.length > 0);
  };

  
  const handleCheckAllChange = (e) => {
    const checked = e.target.checked;
    setCheckAll(checked);
    setSelectedApps(checked ? apps.map(app => String(app.value)) : []);
  };

  useEffect(() => {
    if (userId) {
      loadUserInfo();
      loadApps();
    }
  }, [userId]);

  useEffect(() => {
    
    if (appsLoaded && userLoaded && !permissionsLoaded) {
      loadUserPermissions();
    }
    
    
    if (appsLoaded && userLoaded && permissionsLoaded) {
      setInitialLoading(false);
    }
  }, [appsLoaded, userLoaded, permissionsLoaded]);

  
  useEffect(() => {
    if (isTargetOwner && appsLoaded) {
      const allIds = apps.map(a => String(a.value));
      setSelectedApps(allIds);
      setCheckAll(allIds.length === apps.length);
    }
  }, [isTargetOwner, appsLoaded, apps]);

  
  if (initialLoading) {
    return (
      <div className="background-second-dashboard">
        <section className="main__content-dashbard">
          <div className="container-fluid">
            <Card>
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <div>Đang tải dữ liệu...</div>
              </div>
            </Card>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="background-second-dashboard">
      <section className="main__content-dashbard">
        <div className="container-fluid">
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Card 
                  title={<Space><AppstoreOutlined />List apps</Space>} 
                  bodyStyle={{ background: 'transparent' }} 
                  extra={
                    isTargetOwner ? null : (
                      <Space>
                        <Button 
                          type="primary" 
                          icon={<SaveOutlined />}
                          onClick={savePermissions}
                          loading={loading}
                          size="small"
                        >
                          Lưu quyền
                        </Button>
                        <Button size="small" onClick={() => setSelectedApps(apps.map(a => a.value))}>Chọn tất cả</Button>
                        <Button size="small" onClick={() => setSelectedApps([])}>Bỏ chọn</Button>
                      </Space>
                    )
                  }
                >
                  <div style={{ maxHeight: 650, overflowY: 'auto', overflowX: 'hidden', paddingRight: 4 }}>
                    <Row gutter={[16,16]} style={{ marginLeft: 0, marginRight: 0 }}>
              {apps.map(app => {
                        const isActive = selectedApps.some(id => String(id) === String(app.value));
                        return (
                  <Col xs={24} sm={12} key={`${app.value}-${app.os}`}>
                            <div
                              onClick={() => {
                                if (isTargetOwner) return; 
                                setSelectedApps(prev => {
                                  const set = new Set(prev.map(v => String(v)));
                                  if (isActive) set.delete(String(app.value)); else set.add(String(app.value));
                                  return Array.from(set);
                                });
                              }}
                              style={{
                                borderRadius: 12,
                                padding: 16,
                                background: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                cursor: isTargetOwner ? 'not-allowed' : 'pointer',
                                transition: '0.2s',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                border: '1px solid #ddd',
                                position: 'relative',
                                filter: isActive ? 'none' : 'grayscale(100%)',
                                opacity: isActive ? 1 : 0.8
                              }}
                            >
                              {isActive && (
                                <CheckCircleFilled style={{ position: 'absolute', top: 8, right: 8, color: '#52c41a', fontSize: 18 }} />
                              )}
                              <div className="app-img me-3" style={{ position: 'relative', width: 60, height: 60, borderRadius: 12, overflow: 'hidden' }}>
                                <img src={app.thumb} alt={app.name} style={{ width: 60, height: 60, objectFit: 'cover', display: 'block' }} />
                                <img alt="" src={`/bg-${app.os}.png`} style={{ position: 'absolute', top: 0, left: 0, width: 60, height: 60, borderRadius: 12, pointerEvents: 'none' }} />
                              </div>
                              <div className="app-title" style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, color: '#000', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{app.code} - {app.name}</div>
                              </div>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
              </Card>
            </Col>
          </Row>
        </div>
      </section>
    </div>
  );
}

export default StudioUserPermission;
